/**
 * Pure builders for the item lists ChatCommandMenu renders.
 *
 * Each section of the main view, and each drill-in sub-view, is built by its own
 * function so that the menu's memos stay one line long and every branch lives in
 * a function small enough to read at a glance. The builders are called in the
 * order the sections appear, so `t()` is called in the same order it always was.
 */
import type { TFunction } from "i18next";
import type {
  ChatCommandMenuItem,
  ChatCommandMenuProps,
  ChatCommandMenuSection,
} from "./ChatCommandMenu";

import { PROMPT_ICON, SKILL_ICON, WORKFLOW_ICON } from "@/constants/kindIcons";

/** The views the menu can show: the main sections, or a drill-in sub-view. */
export type CommandMenuView = "main" | "workflows" | "skills";

/** Everything the main view's sections are built from. */
export type MainSectionsInput = Pick<
  ChatCommandMenuProps,
  | "skills"
  | "showWebSearch"
  | "showKnowledgeHub"
  | "showModelSelector"
  | "showPrompts"
  | "showPrompto"
  | "showConfiguration"
  | "showWorkflows"
  | "workflows"
  | "workflowsLabel"
  | "isWebSearchEnabled"
  | "isKnowledgeHubEnabled"
  | "modelSelectorEnabled"
  | "isConfigurationDisabled"
  | "isPromptLocked"
  | "onWebSearchToggle"
  | "onKnowledgeHubToggle"
  | "onModelSelectorToggle"
  | "onBrowsePrompts"
  | "onPrompto"
  | "onOpenConfiguration"
> & {
  t: TFunction;
  /** Opens a drill-in sub-view. */
  openView: (view: CommandMenuView) => void;
};

/** The toggle rows of the Tools section, in display order. */
const buildToolItems = (input: MainSectionsInput): ChatCommandMenuItem[] => {
  const { t } = input;
  const toolItems: ChatCommandMenuItem[] = [];

  if (input.showWebSearch) {
    toolItems.push({
      id: "web-search",
      label: t("chatCommandMenu.items.webSearch"),
      icon: "solar:global-linear",
      isToggle: true,
      toggleValue: input.isWebSearchEnabled,
      onToggle: input.onWebSearchToggle,
    });
  }

  if (input.showKnowledgeHub) {
    toolItems.push({
      id: "knowledge-hub",
      label: t("chatCommandMenu.items.knowledgeHub"),
      icon: "solar:database-linear",
      isToggle: true,
      toggleValue: input.isKnowledgeHubEnabled,
      onToggle: input.onKnowledgeHubToggle,
    });
  }

  if (input.showModelSelector) {
    toolItems.push({
      id: "model-selector",
      label: t("chatCommandMenu.items.modelSelector"),
      icon: "solar:cpu-bolt-linear",
      isToggle: true,
      toggleValue: input.modelSelectorEnabled,
      onToggle: input.onModelSelectorToggle,
    });
  }

  return toolItems;
};

/** The Tools section, or null when no tool row is permitted. */
const buildToolsSection = (input: MainSectionsInput): ChatCommandMenuSection | null => {
  const toolItems = buildToolItems(input);

  if (toolItems.length === 0) return null;

  return {
    id: "tools",
    label: input.t("chatCommandMenu.sections.tools"),
    items: toolItems,
  };
};

/** The action rows of the Prompts section, in display order. */
const buildPromptItems = (input: MainSectionsInput): ChatCommandMenuItem[] => {
  const { t } = input;
  const promptItems: ChatCommandMenuItem[] = [];

  if (input.showPrompts) {
    promptItems.push({
      id: "browse-prompts",
      label: t("chatCommandMenu.items.browsePrompts"),
      icon: PROMPT_ICON,
      isDisabled: input.isPromptLocked,
      onSelect: input.onBrowsePrompts,
    });
  }

  if (input.showPrompto) {
    promptItems.push({
      id: "prompto",
      label: t("chatCommandMenu.items.prompto"),
      icon: "solar:magic-stick-linear",
      onSelect: input.onPrompto,
    });
  }

  return promptItems;
};

/** The Prompts section, or null when no prompt row is permitted. */
const buildPromptsSection = (input: MainSectionsInput): ChatCommandMenuSection | null => {
  const promptItems = buildPromptItems(input);

  if (promptItems.length === 0) return null;

  return {
    id: "prompts",
    label: input.t("chatCommandMenu.sections.prompts"),
    items: promptItems,
  };
};

/**
 * Workflows drill-in entry — a single "Workflows" row that expands into a
 * dedicated sub-view (keeps the main menu uncluttered). The headerless
 * section renders just the row; the list + footer actions live in the
 * sub-view (see buildWorkflowSubItems below).
 */
const buildWorkflowsEntry = (input: MainSectionsInput): ChatCommandMenuSection | null => {
  if (!(input.showWorkflows && input.workflows.length > 0)) return null;

  return {
    id: "workflows-entry",
    label: "",
    items: [
      {
        id: "workflows-open",
        label: input.workflowsLabel || input.t("chatCommandMenu.sections.workflows"),
        icon: WORKFLOW_ICON,
        keepOpenOnSelect: true,
        onSelect: () => input.openView("workflows"),
      },
    ],
  };
};

/**
 * Skills drill-in entry — a single "Skills" row that expands into a
 * dedicated sub-view, mirroring Workflows (keeps the main menu uncluttered).
 * The skill list lives in the sub-view (see buildSkillSubItems); selecting a row
 * inserts an `@s_<id>` reference token via onSelectSkill.
 */
const buildSkillsEntry = (input: MainSectionsInput): ChatCommandMenuSection | null => {
  if (input.skills.length === 0) return null;

  return {
    id: "skills-entry",
    label: "",
    items: [
      {
        id: "skills-open",
        label: input.t("chatCommandMenu.sections.skills"),
        icon: SKILL_ICON,
        keepOpenOnSelect: true,
        onSelect: () => input.openView("skills"),
      },
    ],
  };
};

/** The Configuration section, or null when it is not permitted. */
const buildConfigurationSection = (input: MainSectionsInput): ChatCommandMenuSection | null => {
  if (!input.showConfiguration) return null;

  return {
    id: "configuration",
    label: input.t("chatCommandMenu.sections.configuration"),
    items: [
      {
        id: "configuration",
        label: input.t("chatCommandMenu.items.configuration"),
        icon: "solar:settings-linear",
        isDisabled: input.isConfigurationDisabled,
        onSelect: input.onOpenConfiguration,
      },
    ],
  };
};

const isSection = (section: ChatCommandMenuSection | null): section is ChatCommandMenuSection =>
  section !== null;

/**
 * The main view's sections, in display order: Tools, Prompts, the Workflows and
 * Skills drill-in entries, then Configuration. A section with nothing permitted
 * is left out.
 */
export const buildMainSections = (input: MainSectionsInput): ChatCommandMenuSection[] =>
  [
    buildToolsSection(input),
    buildPromptsSection(input),
    buildWorkflowsEntry(input),
    buildSkillsEntry(input),
    buildConfigurationSection(input),
  ].filter(isSection);

/** Everything the Workflows sub-view's rows are built from. */
export type WorkflowSubItemsInput = Pick<
  ChatCommandMenuProps,
  | "showWorkflows"
  | "skillQuery"
  | "workflows"
  | "activeWorkflowId"
  | "onSelectWorkflow"
  | "onBrowseAllWorkflows"
  | "onCreateWorkflow"
> & { t: TFunction };

/**
 * Items shown inside the Workflows sub-view: the filterable workflow list
 * followed by the "Browse all workflows" / "New workflow" footer actions.
 */
export const buildWorkflowSubItems = (input: WorkflowSubItemsInput): ChatCommandMenuItem[] => {
  if (!input.showWorkflows) return [];

  const wq = input.skillQuery.trim().toLowerCase();
  const filtered = input.workflows.filter((w) => {
    if (!wq) return true;

    return w.id.toLowerCase().includes(wq) || w.name.toLowerCase().includes(wq);
  });

  const items: ChatCommandMenuItem[] = filtered.map((w) => ({
    id: `workflow-${w.id}`,
    label: w.name,
    // One neutral glyph keeps the list uniform; per-workflow icons are noisy.
    icon: WORKFLOW_ICON,
    description: w.description || undefined,
    isCurrent: w.id === input.activeWorkflowId,
    onSelect: () => input.onSelectWorkflow(w.id),
  }));

  // Footer actions, mirroring the skills sub-view ("Browse all" / "Add").
  items.push({
    id: "workflows-browse-all",
    label: input.t("chatCommandMenu.workflows.browseAll"),
    icon: "solar:layers-minimalistic-linear",
    dividerBefore: true,
    onSelect: input.onBrowseAllWorkflows,
  });

  if (input.onCreateWorkflow) {
    items.push({
      id: "workflows-create",
      label: input.t("chatCommandMenu.workflows.create"),
      icon: "solar:add-square-linear",
      onSelect: input.onCreateWorkflow,
    });
  }

  return items;
};

/** Everything the Skills sub-view's rows are built from. */
export type SkillSubItemsInput = Pick<
  ChatCommandMenuProps,
  "skills" | "skillQuery" | "onSelectSkill"
>;

/**
 * Items shown inside the Skills sub-view: the filterable skill list. Selecting
 * a row inserts an `@s_<id>` reference token via onSelectSkill.
 */
export const buildSkillSubItems = (input: SkillSubItemsInput): ChatCommandMenuItem[] => {
  if (input.skills.length === 0) return [];

  const sq = input.skillQuery.trim().toLowerCase();
  const filtered = input.skills.filter((s) => {
    if (!sq) return true;

    return s.id.toLowerCase().includes(sq) || s.name.toLowerCase().includes(sq);
  });

  return filtered.map((s) => ({
    id: `skill-${s.id}`,
    label: s.name,
    // One glyph for every skill row, matching the Library's skill cards; the
    // catalogue's per-skill icon is deliberately ignored (see SKILL_ICON).
    icon: SKILL_ICON,
    description: s.description || undefined,
    onSelect: () => input.onSelectSkill(s.id),
  }));
};
